import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { io, Socket } from 'socket.io-client'; // Import Socket.io
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../config/api';

interface Notification {
  _id: string;
  title: string;
  message: string;
  type: string;
  data?: any;
  read: boolean;
  createdAt: string;
}

interface NotificationContextType {
  socket: Socket | null;
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  pushEnabled: boolean;
  subscribeToPush: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  checkPermission: () => string; // 'granted', 'denied', 'default'
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Helper to decode VAPID key
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuth(); // Need User object to get ID
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Axios instance with auth header
  const api = useCallback(() => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
  }, [token]);

  const refreshNotifications = useCallback(async () => {
    if (!token) {
        return;
    }
    try {
      setLoading(true);
      const res = await api().get('/api/notifications');
      if (res.data.success) {
        const notifs = res.data.notifications || [];
        setNotifications(notifs);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('NotificationContext: Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  // Initial load & Socket Connection
  useEffect(() => {
    if (token && user) {
      refreshNotifications();

      // Setup Socket.io
      if (!socketRef.current) {
          socketRef.current = io(API_BASE_URL, {
              transports: ['websocket'], // Force WebSocket
          });

          socketRef.current.on('connect', () => {
              // Join room based on user ID
               const userId = user.id;
               if (userId) {
                   socketRef.current?.emit('join', userId);
               }
          });

          socketRef.current.on('notification', (newNotif: Notification) => {
              setNotifications(prev => [newNotif, ...prev]);
              setUnreadCount(prev => prev + 1);
              // Play sound?
          });
          
          socketRef.current.on('disconnect', () => {
          });
      }

      // Check if already subscribed to push
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        navigator.serviceWorker.ready.then(registration => {
          registration.pushManager.getSubscription().then(subscription => {
            if (subscription) setPushEnabled(true);
          });
        });
      }

      return () => {
          if (socketRef.current) {
              socketRef.current.disconnect();
              socketRef.current = null;
          }
      };
    }
  }, [token, refreshNotifications, user]);

  const subscribeToPush = async () => {
    if (!('serviceWorker' in navigator)) return;
    
    try {
      // 0. Explicitly request permission first
      const permissionResult = await Notification.requestPermission();
      if (permissionResult !== 'granted') {
        throw new Error('Permission not granted for notifications');
      }

      // 1. Get VAPID Key
      const keyRes = await axios.get(`${API_BASE_URL}/api/notifications/vapid-key`);
      const { publicKey } = keyRes.data;

      // 2. Register SW (if not already) - we assume index.tsx does it, or we do it here
      const register = await navigator.serviceWorker.register('/sw.js');
      
      // 3. Ask Permission & Subscribe
      const subscription = await register.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // 4. Send to Backend
      await api().post('/api/notifications/subscribe', subscription);
      setPushEnabled(true);
    } catch (err) {
      console.error('Failed to subscribe to push', err);
      alert('Failed to enable notifications. Please check site permissions.');
    }
  };

  const markAsRead = async (id: string) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      await api().put(`/api/notifications/${id}/read`);
    } catch (err) {
      console.error(err);
      refreshNotifications(); // Revert on error
    }
  };

  const markAllAsRead = async () => {
    try {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      await api().put('/api/notifications/read-all');
    } catch (err) {
      console.error(err);
      refreshNotifications();
    }
  };

  const checkPermission = () => {
    if (!('Notification' in window)) return 'denied';
    return Notification.permission;
  };

  return (
    <NotificationContext.Provider value={{ 
      socket: socketRef.current,
      notifications,
      unreadCount,
      loading,
      pushEnabled,
      subscribeToPush,
      markAsRead,
      markAllAsRead,
      checkPermission,
      refreshNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

// Removed duplicate hook definition

